import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import { VehicleStore } from "stores";

import List from "../../List";
import Filter from "../../Filter";

import Reducer from "./KraftstoffReducer";
import Indicator from "./KraftstoffIndicator";

import Pipeline from "../Pipeline";
import { sortAsc } from "../helper";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter?: any;
}

const favourites = [2, 5, 6, 9, 10];

const sorter: any = (a: any, b: any) => {
  if (favourites.includes(a.value)) {
    if (favourites.includes(b.value)) {
      return favourites.indexOf(b.value) < favourites.indexOf(a.value) ? 1 : -1;
    }
    return -1;
  }
  if (favourites.includes(b.value)) {
    return 1;
  }
  return b.title < a.title ? 1 : -1;
};

export const SideFilter: React.FC<IFilter> = ({
  entities,
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.kraftstoff) return null;

  const items = Object.values(entities.kraftstoff);

  const itemCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === itemCount ? [] : value;
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { kraftstoff: newValue },
    });
  };

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: `kraftstoff.${id}.label` }, { defaultMessage: name }),
    }))
    .sort(sorter);

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List sort={false} {...{ value, options, setValueHandler, all }} />
    </Box>
  );

  return (
    <Filter
      component={Component}
      title={t({ id: "filter.kraftstoff.label", defaultMessage: "Kraftstoff" })}
      open={false}
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const { state: vehicles } = React.useContext(VehicleStore);

  const setValueHandler = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    // Check if all variants are selected
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { kraftstoff: newValue },
    });
  };

  if (!(entities.kraftstoff && vehicles)) return null;

  const items = Object.values(entities.kraftstoff);

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: `kraftstoff.${id}.label`, defaultMessage: name }),
    }))
    .sort(sortAsc);

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-kraftstoff-simple-label">
        <FormattedMessage
          id="filter.kraftstoff.label"
          defaultMessage="Kraftstoff"
        />
      </InputLabel>
      <Select
        labelId="filter-kraftstoff-simple-label"
        id="filter-kraftstoff-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({
          id: "filter.kraftstoff.label",
          defaultMessage: "Kraftstoff",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"kraftstoff-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        Component: Filter,
        type: "kraftstoff",
        defaultValue: [],
        Reducer,
        Indicator,
      }}
    />
  );
};

export default Container;
